import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc, addDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Upload, Search, Trash2, Copy, ExternalLink, Image as ImageIcon, File,
  Video, FileText, FolderPlus, Grid, List, Check, X, Loader2, Cloud,
  Tag, Calendar, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const CMSMediaPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filterType, setFilterType] = useState('all');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      setLoading(true);
      const mediaQuery = query(
        collection(db, 'cms_media_library'),
        orderBy('uploadedAt', 'desc')
      );
      const snapshot = await getDocs(mediaQuery);
      const mediaData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMedia(mediaData);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      uploadFiles(files);
    }
  }, []);

  const uploadFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    const progressMap = {};
    files.forEach((file, index) => {
      progressMap[index] = { name: file.name, progress: 0, status: 'uploading' };
    });
    setUploadProgress(progressMap);

    try {
      const uploadPromises = files.map(async (file, index) => {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const storageRef = ref(storage, `cms-media/${fileName}`);

        return new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, file);

          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setUploadProgress(prev => ({
                ...prev,
                [index]: { ...prev[index], progress }
              }));
            },
            (error) => {
              setUploadProgress(prev => ({
                ...prev,
                [index]: { ...prev[index], status: 'error' }
              }));
              reject(error);
            },
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              await addDoc(collection(db, 'cms_media_library'), {
                name: file.name,
                url,
                storagePath: `cms-media/${fileName}`,
                type: file.type,
                size: file.size,
                uploadedAt: new Date(),
                tags: [],
              });
              setUploadProgress(prev => ({
                ...prev,
                [index]: { ...prev[index], progress: 100, status: 'complete' }
              }));
              resolve();
            }
          );
        });
      });

      await Promise.all(uploadPromises);
      toast.success(`${files.length} file(s) uploaded successfully`);
      loadMedia();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Some files failed to upload');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress({}), 2000);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    uploadFiles(files);
  };

  const handleDelete = async () => {
    if (!selectedMedia) return;

    try {
      // Delete from storage
      if (selectedMedia.storagePath) {
        const storageRef = ref(storage, selectedMedia.storagePath);
        await deleteObject(storageRef);
      }

      // Delete from Firestore
      await deleteDoc(doc(db, 'cms_media_library', selectedMedia.id));

      toast.success('Media deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedMedia(null);
      loadMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isImage = (type) => type?.startsWith('image/');
  const isVideo = (type) => type?.startsWith('video/');
  const isPDF = (type) => type === 'application/pdf';

  const getFileIcon = (type) => {
    if (isImage(type)) return ImageIcon;
    if (isVideo(type)) return Video;
    if (isPDF(type)) return FileText;
    return File;
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    try {
      for (const id of selectedItems) {
        const item = media.find(m => m.id === id);
        if (item?.storagePath) {
          const storageRef = ref(storage, item.storagePath);
          await deleteObject(storageRef);
        }
        await deleteDoc(doc(db, 'cms_media_library', id));
      }
      toast.success(`${selectedItems.length} file(s) deleted`);
      setSelectedItems([]);
      setBulkDeleteDialogOpen(false);
      loadMedia();
    } catch (error) {
      console.error('Error deleting files:', error);
      toast.error('Failed to delete some files');
    }
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' ||
      (filterType === 'images' && isImage(item.type)) ||
      (filterType === 'videos' && isVideo(item.type)) ||
      (filterType === 'documents' && (isPDF(item.type) || (!isImage(item.type) && !isVideo(item.type))));
    return matchesSearch && matchesType;
  });

  return (
    <div
      className="min-h-screen bg-background"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-primary/20 z-50 flex items-center justify-center border-4 border-dashed border-primary">
          <div className="bg-white p-8 rounded-xl shadow-xl text-center">
            <Cloud className="h-16 w-16 mx-auto text-primary mb-4" />
            <h3 className="text-xl font-semibold">Drop files to upload</h3>
            <p className="text-muted-foreground mt-2">Release to upload your files</p>
          </div>
        </div>
      )}

      {/* Upload Progress Overlay */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="fixed bottom-6 right-6 z-50 w-80 bg-white rounded-lg shadow-xl border p-4 space-y-3">
          <h4 className="font-semibold flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Uploading {Object.keys(uploadProgress).length} file(s)
          </h4>
          {Object.entries(uploadProgress).map(([key, file]) => (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="truncate flex-1 mr-2">{file.name}</span>
                {file.status === 'complete' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : file.status === 'error' ? (
                  <X className="h-4 w-4 text-red-500" />
                ) : (
                  <span className="text-muted-foreground">{file.progress}%</span>
                )}
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${file.status === 'error' ? 'bg-red-500' : file.status === 'complete' ? 'bg-green-500' : 'bg-primary'}`}
                  style={{ width: `${file.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold">Media Library</h1>
            <p className="text-muted-foreground mt-1">{media.length} files • {formatFileSize(media.reduce((acc, m) => acc + (m.size || 0), 0))} total</p>
          </div>
          <div className="flex gap-2">
            {selectedItems.length > 0 && (
              <Button variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedItems.length})
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          ref={dropZoneRef}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Cloud className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Drag & drop files here</h3>
          <p className="text-muted-foreground mt-1">or click to browse • Images, videos, PDFs, documents</p>
        </div>

        {/* Filters & View Toggle */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Tabs value={filterType} onValueChange={setFilterType}>
                  <TabsList>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="images">Images</TabsTrigger>
                    <TabsTrigger value="videos">Videos</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex border rounded-lg overflow-hidden">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Media Grid/List */}
        <Card>
          <CardHeader>
            <CardTitle>Media Files</CardTitle>
            <CardDescription>
              {filteredMedia.length} of {media.length} file{media.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Loading media...</p>
              </div>
            ) : filteredMedia.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No media files found</p>
                {(searchTerm || filterType !== 'all') && (
                  <Button variant="link" onClick={() => { setSearchTerm(''); setFilterType('all'); }} className="mt-2">
                    Clear filters
                  </Button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredMedia.map((item) => {
                  const FileIcon = getFileIcon(item.type);
                  const isSelected = selectedItems.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`group relative rounded-lg border bg-card overflow-hidden hover:shadow-lg transition-all ${isSelected ? 'ring-2 ring-primary' : ''}`}
                    >
                      {/* Selection Checkbox */}
                      <button
                        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-primary border-primary text-white' : 'bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100'}`}
                        onClick={(e) => { e.stopPropagation(); toggleSelectItem(item.id); }}
                      >
                        {isSelected && <Check className="h-4 w-4" />}
                      </button>

                      {/* Preview */}
                      <div className="aspect-square bg-muted flex items-center justify-center">
                        {isImage(item.type) ? (
                          <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                        ) : isVideo(item.type) ? (
                          <div className="relative w-full h-full">
                            <video src={item.url} className="w-full h-full object-cover" muted />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <Video className="h-12 w-12 text-white" />
                            </div>
                          </div>
                        ) : (
                          <FileIcon className="h-12 w-12 text-muted-foreground" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        <p className="text-sm font-medium truncate" title={item.name}>{item.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(item.size)}</p>
                      </div>

                      {/* Actions Overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button variant="secondary" size="icon" onClick={() => { setSelectedMedia(item); setPreviewDialogOpen(true); }} title="Preview">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button variant="secondary" size="icon" onClick={() => copyUrl(item.url)} title="Copy URL">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="icon" onClick={() => { setSelectedMedia(item); setDeleteDialogOpen(true); }} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* List View */
              <div className="divide-y">
                {filteredMedia.map((item) => {
                  const FileIcon = getFileIcon(item.type);
                  const isSelected = selectedItems.includes(item.id);
                  return (
                    <div key={item.id} className={`flex items-center gap-4 p-4 hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`}>
                      <button
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-primary border-primary text-white' : 'border-gray-300'}`}
                        onClick={() => toggleSelectItem(item.id)}
                      >
                        {isSelected && <Check className="h-4 w-4" />}
                      </button>
                      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center flex-shrink-0">
                        {isImage(item.type) ? (
                          <img src={item.url} alt={item.name} className="w-full h-full object-cover rounded" />
                        ) : (
                          <FileIcon className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-sm text-muted-foreground">{formatFileSize(item.size)} • {item.type}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedMedia(item); setPreviewDialogOpen(true); }}>
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => copyUrl(item.url)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedMedia(item); setDeleteDialogOpen(true); }}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{selectedMedia?.name}</DialogTitle>
              <DialogDescription>
                {selectedMedia?.type} • {formatFileSize(selectedMedia?.size || 0)}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              {selectedMedia && isImage(selectedMedia.type) ? (
                <img
                  src={selectedMedia.url}
                  alt={selectedMedia.name}
                  className="w-full rounded-lg"
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-12 bg-muted rounded-lg">
                  <File className="h-16 w-16 text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">Preview not available</p>
                  <Button
                    variant="link"
                    onClick={() => window.open(selectedMedia?.url, '_blank')}
                    className="mt-2"
                  >
                    Open in new tab
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => copyUrl(selectedMedia?.url)}>
                <Copy className="h-4 w-4 mr-2" />
                Copy URL
              </Button>
              <Button onClick={() => window.open(selectedMedia?.url, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Media</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{selectedMedia?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {selectedItems.length} Files</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {selectedItems.length} selected file(s)? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete}>
                Delete All
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CMSMediaPage;